import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CardContent, Grid, MenuItem, Stack, TextField, Typography, IconButton } from '@mui/material';
import { IconPlus, IconTrash, IconBox, IconDeviceFloppy } from '@tabler/icons';
import { useHistory } from 'react-router-dom';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const ORIGIN_OPTIONS = [
    { value: '0', label: '0 - Nacional' },
    { value: '1', label: '1 - Estrangeira - importação direta' },
    { value: '2', label: '2 - Estrangeira - mercado interno' },
    { value: '3', label: '3 - Nacional - conteúdo importado > 40%' },
    { value: '4', label: '4 - Nacional - processos produtivos básicos' },
    { value: '5', label: '5 - Nacional - conteúdo importado <= 40%' },
    { value: '6', label: '6 - Estrangeira - importação direta, sem similar nacional' },
    { value: '7', label: '7 - Estrangeira - mercado interno, sem similar nacional' },
    { value: '8', label: '8 - Nacional - conteúdo importado > 70%' }
];

const emptyVariacao = () => ({
    nome_variacao: '',
    valor_venda: '',
    valor_custo: '',
    estoque: '',
    sku: '',
    barcode: '',
    weight: '',
    width: '',
    height: '',
    depth: '',
    controle_estoque: 'none',
    imagens: []
});

const CreateProducts = () => {
    const history = useHistory();

    const [product, setProduct] = useState({
        nome: '',
        category: '',
        brand: '',
        model: '',
        unit_of_measure: '',
        ncm: '',
        cest: '',
        origin: ''
    });

    const [variacoes, setVariacoes] = useState([emptyVariacao()]);
    const [fields, setFields] = useState({});
    const [fieldsConfig, setFieldsConfig] = useState([]);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [models, setModels] = useState([]);
    const [units, setUnits] = useState([]);
    const [appId, setAppId] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadFields();
        loadClassificationData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const loadFields = async () => {
        const res = await api.get(`v1/apps/${appId}/product_fields/`);
        setFieldsConfig(res.data);
    };

    const loadClassificationData = async () => {
        const [categoriesRes, brandsRes, modelsRes, unitsRes] = await Promise.all([
            api.get(`v1/apps/${appId}/categories/`),
            api.get(`v1/apps/${appId}/brands/`),
            api.get(`v1/apps/${appId}/product-models/`),
            api.get(`v1/apps/${appId}/units-of-measure/`)
        ]);
        setCategories(categoriesRes.data || []);
        setBrands(brandsRes.data || []);
        setModels(modelsRes.data || []);
        setUnits(unitsRes.data || []);
    };

    // VARIAÇÕES

    const addVariacao = () => {
        setVariacoes([...variacoes, emptyVariacao()]);
    };

    const updateVariacao = (i, field, value) => {
        const copy = [...variacoes];
        copy[i][field] = value;
        setVariacoes(copy);
    };

    const removeVariacao = (i) => {
        const copy = [...variacoes];
        copy.splice(i, 1);
        setVariacoes(copy);
    };

    const handleImageUpload = (i, files) => {
        const copy = [...variacoes];
        const newImages = Array.from(files).slice(0, 6 - copy[i].imagens.length);
        copy[i].imagens = [...copy[i].imagens, ...newImages];
        setVariacoes(copy);
    };

    const removeImage = (i, index) => {
        const copy = [...variacoes];
        copy[i].imagens.splice(index, 1);
        setVariacoes(copy);
    };

    // SALVAR

    const save = async () => {
        if (!product.nome) {
            setStatus({ type: 'error', message: 'Informe o nome do produto' });
            return;
        }

        setStatus({ type: '', message: '' });
        setSaving(true);

        const formData = new FormData();
        formData.append('product', JSON.stringify(product));

        Object.entries(fields).forEach(([key, value]) => {
            formData.append(`fields[${key}]`, value);
        });

        formData.append('variacoes', JSON.stringify(variacoes.map(({ imagens, ...rest }) => rest)));

        variacoes.forEach((v, i) => {
            v.imagens.forEach((img) => {
                formData.append(`imagens_${i}`, img);
            });
        });

        try {
            await api.post(`v1/apps/${appId}/products/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            history.push('/dashboard/config/products');
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao criar produto' });
            setSaving(false);
        }
    };

    return (
        <Box>
            <PageHeader icon={IconBox} gradient="blue" title="Criar Produto" subtitle="Cadastre um novo produto e suas variações" />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            {/* PRODUTO */}
            <GlassCard sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Nome do Produto"
                                fullWidth
                                required
                                value={product.nome}
                                onChange={(e) => setProduct({ ...product, nome: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                label="Categoria"
                                fullWidth
                                value={product.category}
                                onChange={(e) => setProduct({ ...product, category: e.target.value })}
                            >
                                <MenuItem value="">Nenhuma</MenuItem>
                                {categories.map((c) => (
                                    <MenuItem key={c.id} value={c.id}>
                                        {c.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                select
                                label="Marca"
                                fullWidth
                                value={product.brand}
                                onChange={(e) => setProduct({ ...product, brand: e.target.value })}
                            >
                                <MenuItem value="">Nenhuma</MenuItem>
                                {brands.map((b) => (
                                    <MenuItem key={b.id} value={b.id}>
                                        {b.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                select
                                label="Modelo"
                                fullWidth
                                value={product.model}
                                onChange={(e) => setProduct({ ...product, model: e.target.value })}
                            >
                                <MenuItem value="">Nenhum</MenuItem>
                                {models.map((m) => (
                                    <MenuItem key={m.id} value={m.id}>
                                        {m.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                select
                                label="Unidade de Medida"
                                fullWidth
                                value={product.unit_of_measure}
                                onChange={(e) => setProduct({ ...product, unit_of_measure: e.target.value })}
                            >
                                <MenuItem value="">Nenhuma</MenuItem>
                                {units.map((u) => (
                                    <MenuItem key={u.id} value={u.id}>
                                        {u.code} - {u.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="NCM"
                                fullWidth
                                value={product.ncm}
                                onChange={(e) => setProduct({ ...product, ncm: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="CEST"
                                fullWidth
                                value={product.cest}
                                onChange={(e) => setProduct({ ...product, cest: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                select
                                label="Origem"
                                fullWidth
                                value={product.origin}
                                onChange={(e) => setProduct({ ...product, origin: e.target.value })}
                            >
                                <MenuItem value="">Nenhuma</MenuItem>
                                {ORIGIN_OPTIONS.map((o) => (
                                    <MenuItem key={o.value} value={o.value}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                </CardContent>
            </GlassCard>

            {/* CAMPOS CUSTOM */}
            {fieldsConfig.length > 0 && (
                <GlassCard sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography fontWeight={700} mb={2}>
                            Campos adicionais
                        </Typography>
                        <Grid container spacing={2}>
                            {fieldsConfig.map((f) => (
                                <Grid item xs={12} sm={6} key={f.id}>
                                    <TextField
                                        label={f.title}
                                        required={f.required}
                                        fullWidth
                                        onChange={(e) => setFields({ ...fields, [f.name]: e.target.value })}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </GlassCard>
            )}

            {/* VARIAÇÕES */}
            <GlassCard sx={{ mb: 3 }}>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography fontWeight={700}>Variações</Typography>
                        <Button startIcon={<IconPlus size={18} />} onClick={addVariacao}>
                            Adicionar
                        </Button>
                    </Stack>

                    {variacoes.map((v, i) => (
                        <GlassCard key={i} sx={{ mb: 2 }} hoverable={false}>
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            label="Nome"
                                            fullWidth
                                            required
                                            value={v.nome_variacao}
                                            onChange={(e) => updateVariacao(i, 'nome_variacao', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                        <TextField
                                            label="Custo"
                                            fullWidth
                                            required
                                            value={v.valor_custo}
                                            onChange={(e) => updateVariacao(i, 'valor_custo', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                        <TextField
                                            label="Venda"
                                            fullWidth
                                            required
                                            value={v.valor_venda}
                                            onChange={(e) => updateVariacao(i, 'valor_venda', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                        <TextField
                                            label="Estoque"
                                            required
                                            fullWidth
                                            value={v.estoque}
                                            onChange={(e) => updateVariacao(i, 'estoque', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                        <TextField
                                            label="SKU"
                                            fullWidth
                                            value={v.sku}
                                            onChange={(e) => updateVariacao(i, 'sku', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={1}>
                                        <IconButton color="error" onClick={() => removeVariacao(i)} disabled={variacoes.length === 1}>
                                            <IconTrash size={18} />
                                        </IconButton>
                                    </Grid>

                                    <Grid item xs={6} sm={3}>
                                        <TextField
                                            label="Código de barras"
                                            fullWidth
                                            value={v.barcode}
                                            onChange={(e) => updateVariacao(i, 'barcode', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                        <TextField
                                            label="Peso (kg)"
                                            fullWidth
                                            value={v.weight}
                                            onChange={(e) => updateVariacao(i, 'weight', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={4} sm={2}>
                                        <TextField
                                            label="Largura (cm)"
                                            fullWidth
                                            value={v.width}
                                            onChange={(e) => updateVariacao(i, 'width', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={4} sm={2}>
                                        <TextField
                                            label="Altura (cm)"
                                            fullWidth
                                            value={v.height}
                                            onChange={(e) => updateVariacao(i, 'height', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={4} sm={2}>
                                        <TextField
                                            label="Profund. (cm)"
                                            fullWidth
                                            value={v.depth}
                                            onChange={(e) => updateVariacao(i, 'depth', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <TextField
                                            select
                                            label="Controle de estoque"
                                            fullWidth
                                            value={v.controle_estoque}
                                            onChange={(e) => updateVariacao(i, 'controle_estoque', e.target.value)}
                                            helperText="Lote/Série exigem informar o rastreio na entrada"
                                        >
                                            <MenuItem value="none">Nenhum</MenuItem>
                                            <MenuItem value="lot">Lote</MenuItem>
                                            <MenuItem value="serial">Número de Série</MenuItem>
                                        </TextField>
                                    </Grid>

                                    {/* IMAGENS */}
                                    <Grid item xs={12}>
                                        <input type="file" multiple accept="image/*" onChange={(e) => handleImageUpload(i, e.target.files)} />

                                        <Stack direction="row" spacing={1} mt={1} overflow="auto">
                                            {v.imagens.map((img, idx) => (
                                                <Box key={idx} position="relative">
                                                    <img
                                                        src={URL.createObjectURL(img)}
                                                        alt=""
                                                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => removeImage(i, idx)}
                                                        sx={{ position: 'absolute', top: -5, right: -5, background: '#fff' }}
                                                    >
                                                        <IconTrash size={14} />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </GlassCard>
                    ))}
                </CardContent>
            </GlassCard>

            <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button onClick={() => history.push('/dashboard/config/products')} disabled={saving}>
                    Cancelar
                </Button>
                <Button variant="contained" startIcon={<IconDeviceFloppy size={18} />} onClick={save} disabled={saving}>
                    {saving ? 'Salvando...' : 'Criar Produto'}
                </Button>
            </Stack>
        </Box>
    );
};

export default CreateProducts;
